<template>
  <v-form
    class="d-flex flex-column pa-5 rounded-lg"
    :disabled="executionDisabled || readOnly"
    :style="{ gap: '42px', backgroundColor: '#F9FAFB', position: 'relative' }"
  >
    <div class="d-flex flex-column w-100" :style="{ gap: '24px' }">
      <div v-if="pendingExecution && isCustomerVisitEvent" class="w-100">
        <p class="ma-0">
          Para poder indicar las actividades realizadas, es necesario <br />
          confirmar el evento una vez llegada su fecha de realización
        </p>
      </div>

      <div v-else-if="!isCustomerVisitEvent" class="w-100">
        <p class="ma-0">
          Los eventos de {{ event.type.label.toLowerCase() }} no requieren
          <br />
          indicar las actividades específicas realizadas
        </p>
      </div>

      <div
        v-if="!pendingExecution && isCustomerVisitEvent"
        class="w-100 d-flex flex-column flex-md-row"
        :style="{ gap: '24px' }"
      >
        <div class="w-100 d-flex flex-column" :style="{ gap: '8px' }">
          <div
            class="d-flex align-center justify-space-between"
            :style="{ gap: '4px' }"
          >
            <div>
              <p class="ma-0 font-weight-bold text-left">
                Actividades Realizadas
              </p>
              <p
                v-if="!executionDisabled && !readOnly"
                class="ma-0 font-weight-thin text--secondary text-left"
              >
                Indica cuales actividades se llevaron a cabo durante el evento
              </p>
            </div>
            <v-tooltip
              v-if="!executionDisabled && !readOnly && !isExecutionClosed"
              bottom
            >
              <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                <v-menu
                  v-model="isAddMoreActivitiesOpen"
                  allow-overflow
                  :close-on-click="false"
                  :close-on-content-click="false"
                  content-class="white pa-2"
                  :max-width="380"
                  :min-width="380"
                  offset-y
                  offset-overflow
                  top
                >
                  <template #activator="{ on: menuOn, attrs: menuAttrs }">
                    <v-btn
                      icon
                      color="#AF64FD"
                      v-bind="{ ...tooltipAttrs, ...menuAttrs }"
                      v-on="{ ...tooltipOn, ...menuOn }"
                    >
                      <v-icon>mdi-plus-circle-outline</v-icon>
                    </v-btn>
                  </template>
                  <div class="d-flex flex-column" :style="{ gap: '8px' }">
                    <event-form-activity-selector
                      v-model="extraActivities"
                      :available-activities="availableActivities"
                      :disabled="executionDisabled"
                      hide-selection
                      :read-only="readOnly"
                      subtitle="Indica las actividades realizadas que no fueron planeadas inicialmente"
                      title="Actividades Extra"
                    />
                    <v-btn
                      color="#E20809"
                      dark
                      :elevation="0"
                      small
                      @click="isAddMoreActivitiesOpen = false"
                    >
                      OK
                    </v-btn>
                  </div>
                </v-menu>
              </template>
              <span>Agregar Actividades</span>
            </v-tooltip>
          </div>

          <event-history-activity-summary
            v-model="executionActivities"
            :activities="allActivities"
            :event-activities="event.activities"
            :disabled="executionDisabled"
            :read-only="readOnly || isExecutionClosed"
            @removeExtraSubactivity="onRemoveExtraSubactivity"
          />
        </div>
      </div>

      <v-divider />

      <div>
        <div class="mb-2">
          <p class="ma-0 font-weight-bold text-left">Comentarios</p>
          <p class="ma-0 font-weight-thin text--secondary text-left">
            Historial de comentarios relacionados al evento
          </p>
        </div>
        <event-history-comments :event="event" />
      </div>
    </div>
  </v-form>
</template>

<script>
  import EventHistoryActivitySummary from './EventHistoryActivitySummary.vue';
  import EventFormActivitySelector from './EventFormActivitySelector.vue';
  import EventHistoryComments from './EventHistoryComments.vue';
  import { EventType } from '@/core/constants/schedule/event';

  import { mapGetters } from 'vuex';

  export default {
    name: 'EventHistory',
    components: {
      EventHistoryActivitySummary,
      EventHistoryComments,
      EventFormActivitySelector,
    },
    props: {
      event: {
        type: Object,
        required: true,
      },
      pendingExecution: {
        type: Boolean,
        default: false,
      },
      executionDisabled: {
        type: Boolean,
        default: false,
      },
      readOnly: {
        type: Boolean,
        default: false,
      },
    },
    data: () => {
      return {
        extraActivities: [],
        executionActivities: [],
        isAddMoreActivitiesOpen: false,
      };
    },
    computed: {
      ...mapGetters('schedule', ['eventTypes']),
      isExecutionClosed() {
        return !!this.event.execution?.closed;
      },
      isCustomerVisitEvent() {
        return this.event.type.value === EventType.CUSTOMER_VISIT;
      },
      availableActivities() {
        const allActivities =
          this.eventTypes.find((t) => t.value === EventType.CUSTOMER_VISIT)
            ?.activities ?? [];

        const plannedSubactivities = new Set(
          this.event.activities?.flatMap((a) =>
            a.subactivities.map((s) => s.value)
          ) ?? []
        );

        return allActivities
          .map((activity) => ({
            ...activity,
            subactivities: activity.subactivities.filter(
              (s) => !plannedSubactivities.has(s.value)
            ),
          }))
          .filter((a) => a.subactivities.length > 0);
      },
      allActivities() {
        if (this.event.execution && this.isExecutionClosed) {
          if (this.event.execution.subactivities?.length) {
            const grouped = this.executionActivitiesGrouped;
            const executionSubValues = new Set(
              this.event.execution.subactivities.map((s) => s.value)
            );

            this.event.activities?.forEach((eventActivity) => {
              const missedSubs = eventActivity.subactivities.filter(
                (s) => !executionSubValues.has(s.value)
              );

              if (!missedSubs.length) return;

              const existing = grouped.find(
                (g) =>
                  g.activity.value === eventActivity.activity.value &&
                  !g.isExtra
              );

              if (existing) {
                existing.subactivities = [
                  ...existing.subactivities,
                  ...missedSubs,
                ].sort((a, b) => a.label.localeCompare(b.label));
              } else {
                grouped.push({
                  activity: eventActivity.activity,
                  subactivities: [...missedSubs].sort((a, b) =>
                    a.label.localeCompare(b.label)
                  ),
                  isExtra: false,
                });
              }
            });

            return [
              ...grouped
                .filter((g) => !g.isExtra)
                .sort((a, b) =>
                  a.activity.label.localeCompare(b.activity.label)
                ),
              ...grouped
                .filter((g) => g.isExtra)
                .sort((a, b) =>
                  a.activity.label.localeCompare(b.activity.label)
                ),
            ];
          }

          return [...(this.event.activities ?? [])].sort((a, b) =>
            a.activity.label.localeCompare(b.activity.label)
          );
        }

        const eventActivities = this.event.activities ?? [];
        const extraNormalized = this.extraActivities.map((a) => ({
          activity: {
            value: a.activityType,
            label:
              this.availableActivities.find((av) => av.value === a.activityType)
                ?.label ?? a.activityType,
          },
          subactivities: a.subactivities
            .map((subValue) => ({
              value: subValue,
              label:
                this.availableActivities
                  .find((av) => av.value === a.activityType)
                  ?.subactivities?.find((s) => s.value === subValue)?.label ??
                subValue,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        }));

        return [
          ...eventActivities.sort((a, b) =>
            a.activity.label.localeCompare(b.activity.label)
          ),
          ...extraNormalized.sort((a, b) =>
            a.activity.label.localeCompare(b.activity.label)
          ),
        ];
      },
      executionActivitiesGrouped() {
        if (!this.event.execution) return [];

        const executionSubactivities = this.event.execution.subactivities;
        const allActivities =
          this.eventTypes.find((t) => t.value === EventType.CUSTOMER_VISIT)
            ?.activities ?? [];

        const plannedSubactivities = new Set(
          this.event.activities?.flatMap((a) =>
            a.subactivities.map((s) => s.value)
          ) ?? []
        );

        const plannedGrouped = new Map();
        const extraGrouped = new Map();

        executionSubactivities.forEach((sub) => {
          const parentActivity = allActivities.find((a) =>
            a.subactivities.some((s) => s.value === sub.value)
          );

          const isExtra = !plannedSubactivities.has(sub.value);
          const targetMap = isExtra ? extraGrouped : plannedGrouped;

          const key = parentActivity?.value ?? 'unknown';
          const label = parentActivity?.label ?? sub.label;

          if (!targetMap.has(key)) {
            targetMap.set(key, {
              activity: { value: key, label },
              subactivities: [],
              isExtra,
            });
          }

          targetMap.get(key).subactivities.push(sub);
        });

        return [...plannedGrouped.values(), ...extraGrouped.values()];
      },
    },
    watch: {
      event: {
        immediate: true,
        handler(event) {
          this.extraActivities = [];

          const executionSubactivities = event?.execution?.subactivities ?? [];
          const hasExecution = !!event?.execution;
          const isExecutionOpen = hasExecution && !event.execution?.closed;

          if (isExecutionOpen && !executionSubactivities.length) {
            this.executionActivities =
              event.activities?.flatMap((a) =>
                a.subactivities.map((s) => s.value)
              ) ?? [];
          } else {
            this.executionActivities = executionSubactivities.map(
              (s) => s.value
            );
          }
        },
      },
      extraActivities: {
        deep: true,
        handler(activities) {
          const extraSubactivities = activities.flatMap((a) => a.subactivities);
          const newSubactivities = extraSubactivities.filter(
            (s) => !this.executionActivities.includes(s)
          );
          if (newSubactivities.length) {
            this.executionActivities = [
              ...this.executionActivities,
              ...newSubactivities,
            ];
          }
        },
      },
    },
    methods: {
      onRemoveExtraSubactivity({ activityType, subValue }) {
        const activityIndex = this.extraActivities.findIndex(
          (a) => a.activityType === activityType
        );
        if (activityIndex === -1) return;

        const updated = [...this.extraActivities];
        updated[activityIndex] = {
          ...updated[activityIndex],
          subactivities: updated[activityIndex].subactivities.filter(
            (s) => s !== subValue
          ),
        };

        if (!updated[activityIndex].subactivities.length) {
          updated.splice(activityIndex, 1);
        }

        this.extraActivities = updated;
        this.executionActivities = this.executionActivities.filter(
          (s) => s !== subValue
        );
      },
    },
  };
</script>

<style scoped></style>
